import React, { useState, useEffect } from 'react';
import {
  CogIcon,
  GlobeAltIcon,
  CurrencyDollarIcon,
  BellIcon,
  ShieldCheckIcon,
  CreditCardIcon,
  TruckIcon,
  ChartBarIcon,
  UserGroupIcon,
  PhotoIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useGlobalConfig } from '../../contexts/GlobalConfigContext';
import toast from 'react-hot-toast';
import { marketplaceApi } from '../../api/marketplace';

interface MarketplaceSettings {
  integrationEnabled: boolean;
  autoSync: boolean;
  commissionRate: number;
  preferredPaymentMethods: string[];
  shippingPreferences: {
    localPickup: boolean;
    shipping: boolean;
    shippingZones: string[];
  };
  notificationSettings: {
    newOrders: boolean;
    orderUpdates: boolean;
    lowStock: boolean;
    priceChanges: boolean;
  };
  displaySettings: {
    showPrices: boolean;
    showContactInfo: boolean;
    showLocation: boolean;
    enableReviews: boolean;
  };
}

export default function MarketplaceSettings() {
  const { user } = useAuth();
  const { config } = useGlobalConfig();
  const [settings, setSettings] = useState<MarketplaceSettings>({
    integrationEnabled: false,
    autoSync: false,
    commissionRate: 0,
    preferredPaymentMethods: [],
    shippingPreferences: {
      localPickup: true,
      shipping: false,
      shippingZones: [],
    },
    notificationSettings: {
      newOrders: true,
      orderUpdates: true,
      lowStock: true,
      priceChanges: false,
    },
    displaySettings: {
      showPrices: true,
      showContactInfo: true,
      showLocation: true,
      enableReviews: true,
    },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingIntegration, setTestingIntegration] = useState(false);

  const facilityId = 'default'; // Use a default facility ID, backend will handle facility resolution

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await marketplaceApi.getMarketplaceSettings(facilityId);
      setSettings(response);
      setLoading(false);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await marketplaceApi.updateMarketplaceSettings(facilityId, settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestIntegration = async () => {
    try {
      setTestingIntegration(true);
      const result = await marketplaceApi.testMarketplaceIntegration(facilityId);
      toast.success('Integration test completed successfully');
    } catch (error) {
      console.error('Error testing integration:', error);
      toast.error('Integration test failed');
    } finally {
      setTestingIntegration(false);
    }
  };

  const updateSettings = (path: string, value: any) => {
    setSettings(prev => {
      const newSettings = { ...prev };
      const keys = path.split('.');
      let current: any = newSettings;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
  };

  const paymentMethods = [
    { id: 'cash', name: 'Cash', icon: CurrencyDollarIcon },
    { id: 'bank_transfer', name: 'Bank Transfer', icon: CreditCardIcon },
    { id: 'paypal', name: 'PayPal', icon: CreditCardIcon },
    { id: 'stripe', name: 'Stripe', icon: CreditCardIcon },
    { id: 'crypto', name: 'Cryptocurrency', icon: CurrencyDollarIcon },
  ];

  const shippingZones = [
    { id: 'local', name: 'Local Pickup' },
    { id: 'national', name: 'National Shipping' },
    { id: 'international', name: 'International Shipping' },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Marketplace Settings</h2>
          <p className="text-sm text-slate-500">
            Configure your marketplace preferences and integration settings
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleTestIntegration}
            disabled={testingIntegration}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-slate-700 bg-white hover:bg-slate-50/80 disabled:opacity-50"
          >
            {testingIntegration ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                Testing...
              </>
            ) : (
              <>
                <GlobeAltIcon className="h-4 w-4 mr-2" />
                Test Integration
              </>
            )}
          </button>
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <CheckCircleIcon className="h-4 w-4 mr-2" />
                Save Settings
              </>
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* General Settings */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center mb-4">
            <CogIcon className="h-5 w-5 text-slate-400 mr-2" />
            <h3 className="text-lg font-medium text-slate-900">General Settings</h3>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-slate-700">Enable Marketplace Integration</label>
                <p className="text-xs text-slate-500">Allow your listings to appear on external platforms</p>
              </div>
              <button
                onClick={() => updateSettings('integrationEnabled', !settings.integrationEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.integrationEnabled ? 'bg-cyan-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.integrationEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-slate-700">Auto-Sync Listings</label>
                <p className="text-xs text-slate-500">Automatically sync listings across platforms</p>
              </div>
              <button
                onClick={() => updateSettings('autoSync', !settings.autoSync)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.autoSync ? 'bg-cyan-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.autoSync ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Commission Rate (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={settings.commissionRate}
                onChange={(e) => updateSettings('commissionRate', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-slate-500 mt-1">
                Platform commission rate for marketplace sales
              </p>
            </div>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center mb-4">
            <CreditCardIcon className="h-5 w-5 text-slate-400 mr-2" />
            <h3 className="text-lg font-medium text-slate-900">Payment Methods</h3>
          </div>
          
          <div className="space-y-3">
            {paymentMethods.map((method) => (
              <div key={method.id} className="flex items-center">
                <input
                  type="checkbox"
                  id={method.id}
                  checked={settings.preferredPaymentMethods.includes(method.id)}
                  onChange={(e) => {
                    const newMethods = e.target.checked
                      ? [...settings.preferredPaymentMethods, method.id]
                      : settings.preferredPaymentMethods.filter(m => m !== method.id);
                    updateSettings('preferredPaymentMethods', newMethods);
                  }}
                  className="h-4 w-4 text-cyan-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor={method.id} className="ml-3 text-sm text-slate-700">
                  {method.name}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Shipping Preferences */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center mb-4">
            <TruckIcon className="h-5 w-5 text-slate-400 mr-2" />
            <h3 className="text-lg font-medium text-slate-900">Shipping Preferences</h3>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-slate-700">Local Pickup</label>
                <p className="text-xs text-slate-500">Allow customers to pick up items locally</p>
              </div>
              <button
                onClick={() => updateSettings('shippingPreferences.localPickup', !settings.shippingPreferences.localPickup)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.shippingPreferences.localPickup ? 'bg-cyan-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.shippingPreferences.localPickup ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-slate-700">Shipping</label>
                <p className="text-xs text-slate-500">Offer shipping options to customers</p>
              </div>
              <button
                onClick={() => updateSettings('shippingPreferences.shipping', !settings.shippingPreferences.shipping)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.shippingPreferences.shipping ? 'bg-cyan-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.shippingPreferences.shipping ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {settings.shippingPreferences.shipping && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Shipping Zones
                </label>
                <div className="space-y-2">
                  {shippingZones.map((zone) => (
                    <div key={zone.id} className="flex items-center">
                      <input
                        type="checkbox"
                        id={zone.id}
                        checked={settings.shippingPreferences.shippingZones.includes(zone.id)}
                        onChange={(e) => {
                          const newZones = e.target.checked
                            ? [...settings.shippingPreferences.shippingZones, zone.id]
                            : settings.shippingPreferences.shippingZones.filter(z => z !== zone.id);
                          updateSettings('shippingPreferences.shippingZones', newZones);
                        }}
                        className="h-4 w-4 text-cyan-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor={zone.id} className="ml-3 text-sm text-slate-700">
                        {zone.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center mb-4">
            <BellIcon className="h-5 w-5 text-slate-400 mr-2" />
            <h3 className="text-lg font-medium text-slate-900">Notifications</h3>
          </div>
          
          <div className="space-y-4">
            {Object.entries(settings.notificationSettings).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-slate-700">
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </label>
                  <p className="text-xs text-slate-500">
                    Get notified about {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                  </p>
                </div>
                <button
                  onClick={() => updateSettings(`notificationSettings.${key}`, !value)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    value ? 'bg-cyan-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      value ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Display Settings */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center mb-4">
            <PhotoIcon className="h-5 w-5 text-slate-400 mr-2" />
            <h3 className="text-lg font-medium text-slate-900">Display Settings</h3>
          </div>
          
          <div className="space-y-4">
            {Object.entries(settings.displaySettings).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-slate-700">
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </label>
                  <p className="text-xs text-slate-500">
                    {key === 'showPrices' && 'Display prices on listings'}
                    {key === 'showContactInfo' && 'Show contact information'}
                    {key === 'showLocation' && 'Display location information'}
                    {key === 'enableReviews' && 'Allow customer reviews'}
                  </p>
                </div>
                <button
                  onClick={() => updateSettings(`displaySettings.${key}`, !value)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    value ? 'bg-cyan-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      value ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Security & Privacy */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center mb-4">
            <ShieldCheckIcon className="h-5 w-5 text-slate-400 mr-2" />
            <h3 className="text-lg font-medium text-slate-900">Security & Privacy</h3>
          </div>
          
          <div className="space-y-4">
            <div className="p-4 bg-cyan-50 rounded-lg">
              <div className="flex">
                <ShieldCheckIcon className="h-5 w-5 text-blue-400 mr-2" />
                <div>
                  <h4 className="text-sm font-medium text-cyan-800">Data Protection</h4>
                  <p className="text-sm text-cyan-700 mt-1">
                    Your marketplace data is encrypted and protected according to industry standards.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="flex">
                <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
                <div>
                  <h4 className="text-sm font-medium text-green-800">GDPR Compliant</h4>
                  <p className="text-sm text-green-700 mt-1">
                    We comply with GDPR regulations for data protection and privacy.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
